import { EditIcon } from '@chakra-ui/icons'
import { Box, Flex, Text } from '@chakra-ui/react'
import { useApp, useCachedUser, usePracticeAccess } from '@hb/react'
import {
  PracticeVisitDraft,
  SubmittedAuthentication,
  SubmittedVisitData,
  User,
  VISIT_DRAFTS,
  WithId,
  colors,
  practiceAdminRoles,
} from '@hb/shared'
import { deleteDoc, doc, setDoc } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { addPracticeVisit, db, useDocument } from '../../../backend'
import { ActionButton, DeleteButton } from '../../../components/Buttons'
import { SimpleForm } from '../../../components/forms'
import { Loading } from '../../../components/Loading'
import { DefaultModal } from '../../../components/Modals'
import { AuthenticateAndSubmit } from '../../AuthenticateCheckbox'
import { newVisitField } from '../fields'
import { populateVisitDraft } from '../utils'
import { VisitDraftView } from './VisitView'

export const VisitDraftViewModal = ({
  draft,
  loading,
  patient: patientProp,
  onClose,
}: {
  draft: WithId<PracticeVisitDraft> | null
  patient?: WithId<User> | null
  loading?: boolean
  onClose: () => void
}) => {
  const draftId = useMemo(() => draft?.id ?? null, [draft])
  const practiceId = useMemo(() => draft?.practiceId ?? null, [draft])
  const fetchedPatientId = useMemo(
    () => (patientProp === undefined ? null : (draft?.patientId ?? null)),
    [patientProp, draft],
  )
  const { data: fetchedPatient } = useCachedUser(fetchedPatientId)
  const patient = useMemo(
    () => (patientProp === undefined ? fetchedPatient : patientProp),
    [patientProp, fetchedPatient],
  )

  const { practiceAccess } = usePracticeAccess()
  const canAuthenticate = useMemo(() => {
    const practiceId = draft?.practiceId ?? null
    if (!practiceId) return false
    const practiceRole = practiceAccess?.[practiceId]
    if (!practiceRole) return false
    return practiceAdminRoles.includes(practiceRole)
  }, [practiceAccess, draft])

  const [editing, setEditing] = useState(false)
  const [authChecked, setAuthChecked] = useState(false)

  const onEditDraft = useCallback(
    async (updated: SubmittedVisitData) => {
      if (!patient) return { error: 'No patient selected' }
      if (!draftId) return { error: 'No draft selected' }
      if (!practiceId) return { error: 'No practice selected' }
      try {
        const populated = populateVisitDraft(updated, practiceId, patient)
        await setDoc(doc(db, VISIT_DRAFTS, draftId), populated)
        setEditing(false)
        return { success: 'Draft updated' }
      } catch (e: any) {
        return { error: e.message || 'error' }
      }
    },
    [draftId, patient, practiceId],
  )

  const [submitting, setSubmitting] = useState(false)
  const { appName } = useApp()
  const onSignAndSubmit = useCallback(
    async (authentication: SubmittedAuthentication): Promise<void> => {
      if (!draftId || !draft) {
        throw new Error('No draft selected')
      }
      setSubmitting(true)
      return addPracticeVisit({
        appName,
        draftId,
        visitId: null,
        authentication,
        ...draft,
      })
        .then(() => {
          onClose()
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
    [appName, draft, onClose, draftId],
  )

  const onDelete = useCallback(async () => {
    if (!draftId) return { error: 'No draft selected' }
    try {
      await deleteDoc(doc(db, VISIT_DRAFTS, draftId))
      onClose()
      return { success: 'Draft deleted' }
    } catch (e: any) {
      return { error: e.message || 'error' }
    }
  }, [draftId, onClose])
  let body: React.JSX.Element = <> </>
  if (draft) {
    body = editing ? (
      <SimpleForm
        field={newVisitField}
        submitText="Save Edited Draft"
        onCancel={() => setEditing(false)}
        value={draft}
        onSubmit={onEditDraft}
      />
    ) : (
      <Flex flexFlow="column">
        <Box w="100%" borderRadius={6} bg="white" boxShadow="1px 1px 4px #00000055" p={3}>
          <VisitDraftView draft={draft} />
        </Box>
        <Flex px={2} w="100%" gap={2} mb={2} mt={3} align="center">
          <ActionButton
            isDisabled={submitting}
            flex={1}
            bg="white"
            gap={2}
            onClick={() => setEditing(true)}>
            <EditIcon color={colors.green.hex} />
            <Text>Edit Draft</Text>
          </ActionButton>
          <DeleteButton
            text="Delete Draft"
            borderColor="red.600"
            bg="white"
            isDisabled={submitting}
            borderWidth="1px"
            itemName="draft"
            onDelete={onDelete}
          />
        </Flex>
        {canAuthenticate && practiceId ? (
          <AuthenticateAndSubmit
            authChecked={authChecked}
            onChange={setAuthChecked}
            itemName="visit"
            onSubmit={onSignAndSubmit}
            practiceId={practiceId}
          />
        ) : null}
      </Flex>
    )
  } else if (loading) {
    body = <Loading text="Loading draft..." />
  } else {
    body = (
      <Text p={3} color="red.600">
        Could not load draft
      </Text>
    )
  }
  return (
    <DefaultModal
      isOpen
      size="2xl"
      overlayHeader
      contentProps={{ bg: 'gray.50', p: 0 }}
      onClose={() => {
        if (editing) {
          setEditing(false)
        } else {
          onClose()
        }
      }}
      render={() => body}
    />
  )
}

export const VisitDraftIdViewModal = ({
  draftId,
  onClose,
  patient: patientProp,
}: {
  draftId: string
  patient?: WithId<User> | null
  onClose: () => void
}) => {
  const { data: draft, loading } = useDocument<PracticeVisitDraft>(VISIT_DRAFTS, draftId)

  return (
    <VisitDraftViewModal draft={draft} loading={loading} patient={patientProp} onClose={onClose} />
  )
}
